import type {
  ClosedReason,
  Locale,
  Member,
  PublicConfig,
  PublicUserDTO,
  SessionDTO,
  SessionMeta,
} from '@akpl/shared';
import type { StateCreator } from 'zustand';

import type { BridgeKind, DeviceInfo } from '../bridge/types';
import type { TvStore } from './types';

export type BootStatus = 'idle' | 'booting' | 'ready' | 'error';
export type AuthMode = 'firebase' | 'offline';
export type AuthStatus = 'unknown' | 'signed_out' | 'signed_in';

export interface SessionState {
  /**
   * `error` is the raw diagnostic (`GET /config → 503`) and `code` the `AkpError` code behind
   * it. UX-373: the raw line is for `?debug=1`, never for a living-room screen — the code is
   * what the boot error screen turns into a sentence a person can act on.
   */
  boot: { status: BootStatus; error: string | null; code: string | null };
  config: PublicConfig | null;
  device: { id: string; bridge: BridgeKind; info: DeviceInfo | null };
  authMode: AuthMode;
  locale: Locale;
  auth: { status: AuthStatus; uid: string | null; user: PublicUserDTO | null; features: string[] };
  /** D1 session row (from pair/exchange or `/sessions/:sid`). */
  session: SessionDTO | null;
  /** Live RTDB mirror `/sessions/{sid}/meta` (WP1.6 listener writes it). */
  meta: SessionMeta | null;
  members: Record<string, Member>;
  /**
   * Why the session ended, for the TV-11 copy (QA 2026-09-15 UX-566). It outlives
   * `clearSession()` on purpose — the screen that reads it is rendered after the reset — and is
   * `null` when nothing ended, or when the reason is unknown.
   */
  endReason: ClosedReason | null;
}

export interface SessionActions {
  setBoot(status: BootStatus, error?: string | null, code?: string | null): void;
  setConfig(config: PublicConfig | null): void;
  setDevice(device: SessionState['device']): void;
  setAuthMode(mode: AuthMode): void;
  setLocale(locale: Locale): void;
  setAuth(auth: Partial<SessionState['auth']> & { status: AuthStatus }): void;
  setSession(session: SessionDTO | null): void;
  setMeta(meta: SessionMeta | null): void;
  setMembers(members: Record<string, Member> | null): void;
  /** Why the session ended (UX-566); set by `endLiveSession` after `clearSession`. */
  setEndReason(reason: ClosedReason | null): void;
  /** Local sign-out: clears auth + session (Profile logout, session_expired). */
  clearSession(): void;
}

export type SessionSlice = SessionState & SessionActions;

export const initialSessionState: SessionState = {
  boot: { status: 'idle', error: null, code: null },
  config: null,
  device: { id: '', bridge: 'mock', info: null },
  authMode: 'offline',
  locale: 'es',
  auth: { status: 'unknown', uid: null, user: null, features: [] },
  session: null,
  meta: null,
  members: {},
  endReason: null,
};

export const createSessionSlice: StateCreator<TvStore, [], [], SessionSlice> = (set) => ({
  ...initialSessionState,
  setBoot: (status, error = null, code = null) => set({ boot: { status, error, code } }),
  setConfig: (config) => set({ config }),
  setDevice: (device) => set({ device }),
  setAuthMode: (authMode) => set({ authMode }),
  setLocale: (locale) => set({ locale }),
  setAuth: (auth) => set((s) => ({ auth: { ...s.auth, ...auth } })),
  setSession: (session) => set({ session }),
  setMeta: (meta) => set({ meta }),
  setMembers: (members) => set({ members: members ?? {} }),
  setEndReason: (endReason) => set({ endReason }),
  clearSession: () =>
    set({
      auth: { status: 'signed_out', uid: null, user: null, features: [] },
      session: null,
      meta: null,
      members: {},
      endReason: null,
    }),
});

export const selectSid = (s: TvStore): string | null => s.session?.id ?? null;
export const selectIsSignedIn = (s: TvStore): boolean => s.auth.status === 'signed_in';
/** Signed in AND holding an active session → shell shows the rail screens. */
export const selectIsInApp = (s: TvStore): boolean =>
  s.auth.status === 'signed_in' && s.session !== null && s.session.status === 'active';
